import {useCallback, useState} from 'react'
import {announce} from '@github-ui/aria-live'

import {useOptionalEstablishedAhpSession} from '@github-ui/copilot-shared-ui/hooks/use-established-ahp-session'
import {useOptionalEstablishedAhpSessionViewState} from '@github-ui/copilot-shared-ui/hooks/use-established-ahp-session-view'

import {useThreadTitleActions} from './use-timeline-contexts'

export interface ConversationTitleEditing {
  isEditingTitle: boolean
  renamePending: boolean
  onRename: (() => void) | undefined
  onTitleCommit: (next: string) => void
  onTitleCancel: () => void
}

export function useConversationTitleEditing(): ConversationTitleEditing {
  const session = useOptionalEstablishedAhpSession()
  const view = useOptionalEstablishedAhpSessionViewState()
  const {setUserThreadTitle, onSessionRenamed, onUserRename} = useThreadTitleActions()
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [renamePending, setRenamePending] = useState(false)

  const startEditing = useCallback(() => {
    setIsEditingTitle(true)
  }, [])
  const onRename = Boolean(session?.identity.taskId) && !view.passive && !renamePending ? startEditing : undefined

  const onTitleCommit = useCallback(
    async (next: string) => {
      setRenamePending(true)
      try {
        if (!session) throw new Error('An established AHP session is required to rename this conversation.')
        setUserThreadTitle(next)
        // Lock generated naming on user intent, even if the host later rejects
        // it. Canonical remote metadata remains independent of that tool lock.
        onUserRename?.()
        const acceptance = session.commands.renameSession(next, {waitForAcceptance: true})
        announce('Saving the conversation name')
        await acceptance
        const snapshot = session.view.getSnapshot()
        const confirmedTitle = snapshot.sessionState?.title ?? snapshot.state?.title
        if (session.identity.taskId) onSessionRenamed?.(session.identity.taskId, confirmedTitle || next)
      } catch {
        announce('The new conversation name could not be saved.', {assertive: true})
      } finally {
        setUserThreadTitle('')
        setRenamePending(false)
        setIsEditingTitle(false)
      }
    },
    [onSessionRenamed, onUserRename, session, setUserThreadTitle],
  )

  const onTitleCancel = useCallback(() => {
    setIsEditingTitle(false)
  }, [])

  return {isEditingTitle, renamePending, onRename, onTitleCommit, onTitleCancel}
}
